// Simplified field types for detection - no hierarchical structures
// Complete list of field types for detection
const allFieldTypes = [
'TextField',
'NumericField',
'YesNoField',
'DateTimeField',
'Label',
'TimeField',
'ChoiceField',
'MultiChoiceField',
'AddressField',
'SignatureField',
'PhotoField'  // Added PhotoField back to the detection list
// Removing hierarchical structures:
// 'Section', 
// 'SectionEnd',
// 'Repeatable',
// 'RepeatableEnd'
];

// All field types are supported - we don't have any hierarchical structures
const supportedFieldTypes = allFieldTypes;

// Function to generate a customized prompt
const generateFormAnalysisPrompt = (options = {}) => {
// Default options
const defaults = {
includeFieldTypes: allFieldTypes, // Use ALL field types for detection
includeConfidence: true,
additionalInstructions: 'Be thorough and look for all possible form fields, including small checkboxes, radio buttons, text areas, and photo placeholders. Pay attention to field labels that may be separated from their input elements.'
 };

// Merge defaults with provided options
const config = { ...defaults, ...options };

// Build field types list
const fieldTypesList = config.includeFieldTypes
 .map(type => ` - ${type}`)
 .join('\n');

// Build output format instructions
const outputFormat = [
'- label: The detected field label',
'- type: The predicted field type'
 ];

if (config.includeConfidence) {
outputFormat.push('- confidence: Your confidence in the type prediction (low, medium, high)');
 }

// Create the full prompt
const prompt = `Analyze this form image. Identify all form fields with their labels and types. 

1. FORM FIELDS: All form fields with their labels and types

Determine the form title, or any other means of determining what kind of form it is, and consider what you know about what similar forms usually contain to help inform the identification of fields to come. Make sure all the fields that you expect are there; if they're not, re-evaluate to make sure that you haven't missed any. All of the fields that you have in the end should make sense in the context of the form title. 

VISUAL PATTERNS TO RECOGNIZE:
- Lines, underlines, dots, or spaces after labels = TextField
- Small squares or boxes = checkboxes (ChoiceField/MultiChoiceField) 
- Circles or bullets = radio buttons (ChoiceField)
- Consistent spacing/alignment = input areas even without borders
- Fill-in-the-blank patterns: "Name ________" or "Date: _______"
- Photo placeholders, image boxes, or "attach photo" areas = PhotoField

FIELD DETECTION RULES:
- Labels can be above, left, or separated by lines from inputs
- Fields with identical layout/spacing are likely same type
- Single checkboxes imply Yes/No pairs - create both options
- In tables/grids: each cell may be separate field OR part of grouped entry
- Similar options clustered together = ChoiceField (single) or MultiChoiceField (multiple)
- In general, we don't want to have ChoiceField with just yes and no as options...those should be YesNoField
- For ChoiceField (single) vs. MultiChoiceField (multiple), try to think logically on where a question (in the real world) could have multiple answers (multi) vs. one single answer (single)
- Photo areas: Look for boxes, rectangles, or areas labeled for photos/images

PHOTO FIELD DETECTION:
- Rectangular boxes that are clearly meant for photos (may contain sample images or be empty)
- Each individual photo box is a separate PhotoField
- Use the text label below or near each photo box as the field name

CONTEXT CLUES:
- % symbols, numbers, measurements = NumericField
- Date/time words = DateTimeField  
- Signature areas = SignatureField
- Photo/image related text = PhotoField
- "Client _____ Date _____" = separate TextField for each blank
- Matrix grids: each intersection needs individual detection

For each field, detect:
- The field label or name
- The most likely field type from this list:
${fieldTypesList}

Pay special attention to:
- Small checkboxes and radio buttons
- Fields with captions or instructions nearby
- Required field indicators (often marked with asterisks or notes)
- All form fields should be identified as flat fields (no hierarchy)
- All text input areas, even if they're not obviously delineated
- Photo placeholder areas or image attachment sections (each individual photo box is a separate field)

For ChoiceField and MultiChoiceField types, also identify:
- All visible options/choices (checkboxes, radio buttons, dropdown items)
- The text label for each option
- Whether it allows single selection (ChoiceField) or multiple (MultiChoiceField)

Be thorough in your analysis:
- Scan the entire form, including headers, footers, and margins
- Pay attention to tables where each row might represent a separate field or entry
- Look for subtle field indicators like underlines, boxes, or lines
- Identify both prominent and less obvious fields
- Note if fields appear to be grouped or related
- Identify any photo or image placeholder areas (treat each photo box as a separate PhotoField)

"CRITICAL: When you identify photo placeholder areas, image boxes, or any rectangular areas meant for photos/images, you MUST return the field type as exactly 'PhotoField' (case-sensitive). Do not use 'TextField' for photo areas."

When the field is a simple checkbox and you identify it as a ChoiceField, ensure that you have choices for the unchecked state and the checked state. One option isn't enough. Either find a nearby checkbox that is its opposite and treat those as a pair of options, or give the checked state and its negation as options. 

Return JSON in this format:
{
  "fields": [
    {
      ${outputFormat.join(',\n      ')}${config.includeConfidence ? '' : ''}${(config.includeFieldTypes.includes('ChoiceField') || config.includeFieldTypes.includes('MultiChoiceField')) ? ',\n      "choices": [{"label": "Option text", "value": "option_value"}]' : ''}
    }
  ]
}

Example for a choice field:
{
  "fields": [
    {
      "label": "Preferred Contact Method",
      "type": "ChoiceField",
      ${config.includeConfidence ? '"confidence": "high",' : ''}
      "choices": [
        { "label": "Email", "value": "email" },
        { "label": "Phone", "value": "phone" },
        { "label": "Text Message", "value": "text" }
      ]
    }
  ]
}

Example for multiple photo fields:
{
  "fields": [
    {
      "label": "Photo #1 Retaining Wall",
      "type": "PhotoField"
    },
    {
      "label": "Photo #2 Retaining wall", 
      "type": "PhotoField"
    }
  ]
}
{
  "fields": [
    {
      "label": "Property Photo",
      "type": "PhotoField",
      ${config.includeConfidence ? '"confidence": "high"' : ''}
    }
  ]
}

${config.additionalInstructions ? '\n' + config.additionalInstructions : ''}

Return ONLY valid JSON.`;

return prompt;
};

// Default prompt with all fields
const formAnalysisPrompt = generateFormAnalysisPrompt();

export {
formAnalysisPrompt,
generateFormAnalysisPrompt,
allFieldTypes,
supportedFieldTypes
};